import { io } from "socket.io-client";

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.isConnecting = false;
    this.connectionQueue = [];
    this.isInitialized = false;
  }

  // Méthode d'initialisation appelée par le middleware
  initialize(token) {
    console.log("Initialisation du service Socket.io");
    this.isInitialized = true;

    // Ne pas tenter de connexion si nous n'avons pas de token
    if (!token) {
      console.warn("Socket.io: aucun token fourni, connexion différée");
      return;
    }

    this.connect(token);

    // Exposer le service à la fenêtre pour un accès facile par d'autres modules
    window.webSocketService = this;
  }

  connect(token) {
    // Éviter les connexions multiples
    if (this.socket || this.isConnecting) {
      console.log("Socket.io déjà connecté ou en cours de connexion");

      // Si une connexion est en cours, ajouter le token à la file d'attente
      if (this.isConnecting && token) {
        this.connectionQueue.push(token);
      }
      return;
    }

    try {
      this.isConnecting = true;

      const socketUrl =
        import.meta.env.VITE_API_URL || "https://baobab-exchange-backend.onrender.com";
      const baseUrl = socketUrl.replace("/api", "");

      console.log("Tentative de connexion Socket.io à:", baseUrl);

      this.socket = io(baseUrl, {
        path: "/socket.io/",
        auth: { token },
        transports: ["websocket", "polling"],
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 3000,
        timeout: 10000,
        autoConnect: false, // Connexion manuelle pour plus de contrôle
      });

      // Configuration des événements socket avant la connexion
      this.setupSocketEvents();

      // Connexion manuelle
      this.socket.connect();
    } catch (error) {
      console.error("Erreur lors de la configuration Socket.io:", error);
      this.notifyListeners("error", { error: error.message });
      this.isConnecting = false;
    }
  }

  setupSocketEvents() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("Socket.io connecté, ID:", this.socket.id);
      this.reconnectAttempts = 0;
      this.isConnecting = false;

      // Traiter la file d'attente des connexions
      while (this.connectionQueue.length > 0) {
        const queuedToken = this.connectionQueue.shift();
        if (queuedToken && this.socket) {
          this.socket.auth = { token: queuedToken };
          // Pas besoin de reconnecter, juste mettre à jour le token
        }
      }

      this.notifyListeners("connection", {
        status: "connected",
        socketId: this.socket.id,
      });
    });

    this.socket.on("welcome", (data) => {
      console.log("Message de bienvenue reçu:", data);
      this.notifyListeners("welcome", data);
    });

    this.socket.on("message", (data) => {
      console.log("Message reçu:", data);
      this.notifyListeners(data.type || "message", data);
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Socket.io déconnecté, raison:", reason);
      this.isConnecting = false;
      this.notifyListeners("connection", { status: "disconnected", reason });
    });

    this.socket.on("connect_error", (error) => {
      console.error("Erreur de connexion Socket.io:", error.message);
      this.isConnecting = false;
      this.notifyListeners("error", { error: error.message });

      // Limiter les tentatives de reconnexion
      this.reconnectAttempts++;
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error(
          `Maximum de ${this.maxReconnectAttempts} tentatives de reconnexion atteint. Abandon.`,
        );
        this.socket.disconnect();
      }
    });

    this.socket.on("error", (error) => {
      console.error("Erreur Socket.io:", error);
      this.isConnecting = false;
      this.notifyListeners("error", { error });
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
      console.log("Socket.io déconnecté manuellement");
    }
  }

  registerHandler(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }

    // Si le service n'est pas initialisé, avertir mais continuer
    if (!this.isInitialized) {
      console.warn(
        "WebSocketService: enregistrement d'un gestionnaire avant l'initialisation du service",
      );
    }

    this.listeners.get(eventType).push(callback);

    return () => {
      const callbacks = this.listeners.get(eventType) || [];
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    };
  }

  notifyListeners(eventType, data) {
    try {
      const callbacks = this.listeners.get(eventType) || [];
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (err) {
          console.error(
            `Erreur dans le gestionnaire d'événement ${eventType}:`,
            err,
          );
        }
      });

      // Notifications globales pour tous les événements
      const globalCallbacks = this.listeners.get("*") || [];
      globalCallbacks.forEach((callback) => {
        try {
          callback({ type: eventType, data });
        } catch (err) {
          console.error("Erreur dans le gestionnaire d'événement global:", err);
        }
      });
    } catch (error) {
      console.error("Erreur lors de la notification des écouteurs:", error);
    }
  }

  sendMessage(type, data) {
    if (this.socket && this.socket.connected) {
      try {
        const message = { type, ...data };
        this.socket.emit("message", message);
        console.log("Message envoyé:", message);
        return true;
      } catch (error) {
        console.error("Erreur lors de l'envoi du message:", error);
        return false;
      }
    } else {
      console.error("Socket.io non connecté, impossible d'envoyer le message");
      return false;
    }
  }

  // Vérifier l'état de la connexion
  isConnected() {
    return this.socket && this.socket.connected;
  }

  // Reconnecter avec un nouveau token
  reconnect(token) {
    if (this.socket) {
      this.disconnect();
    }
    setTimeout(() => this.connect(token), 500);
  }
}

export const webSocketService = new WebSocketService();

// Exposer le service à la fenêtre pour un accès facile par d'autres modules
if (typeof window !== "undefined") {
  window.webSocketService = webSocketService;
}

export default webSocketService;
